import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Typography,
  Grid,
  Stack,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Paper,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import { fr } from 'date-fns/locale';
import { startOfWeek, endOfWeek, startOfDay, endOfDay, startOfMonth, endOfMonth, format, addDays } from 'date-fns';
import { Clear as ClearIcon, ViewList as ViewListIcon, CalendarMonth as CalendarIcon } from '@mui/icons-material';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import ActivityCard from '../components/activities/ActivityCard';
import axios from '../utils/axios';
import { isFrenchPublicHoliday } from '../utils/dates';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-hot-toast';
import MobileActivityList from '../components/activities/MobileActivityList';
import useActivityTypes from '../hooks/useActivityTypes';
import { buildCalendarEvent } from '../utils/activity';

// Setup the localizer for react-big-calendar
const locales = {
  'fr': fr
};

const localizer = dateFnsLocalizer({
  format: (date, formatStr) => format(date, formatStr, { locale: fr }),
  parse: (str) => new Date(str),
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 1 }),
  getDay: (date) => date.getDay(),
  locales,
  messages: {
    previous: 'Précédent',
    next: 'Suivant',
    today: 'Aujourd\'hui',
    month: 'Mois',
    week: 'Semaine',
    day: 'Jour',
    agenda: 'Agenda',
    date: 'Date',
    time: 'Heure',
    event: 'Événement',
  },
});

// Fallback colours / labels if /api/activity-types is unavailable
const FALLBACK_CATEGORY_COLORS = {
  'Yoga Ashtanga': '#032D23',
  'Hatha Yoga': '#B8860B',
  'Yoga Vinyasa': '#404616',
  'Yoga Restauratif': '#452663',
  'Yin Yoga': '#CF5200',
  'Yoga des Énergies': '#4A601C',
  'Shiatsu': '#890857',
  'Chi Nei Tsang': '#B75312',
  'Soin': '#792720',
  'Ateliers': '#0F52BA',
  'Pilates Doux': '#6D2348',
  'Power Pilates': '#432C5F',
  'Pilates - Tous niveaux': '#6D2348',
  'Autres': '#00533E'
};

const FALLBACK_CATEGORY_DISPLAY_NAMES = {
  'Yoga Ashtanga': 'Yoga Ashtanga',
  'Hatha Yoga': 'Hatha Yoga',
  'Yoga Vinyasa': 'Yoga Vinyasa',
  'Yoga Restauratif': 'Yoga Restauratif',
  'Yin Yoga': 'Yin Yoga',
  'Yoga des Énergies': 'Yoga des Énergies',
  'Shiatsu': 'Shiatsu',
  'Chi Nei Tsang': 'CNT',
  'Soin': 'Soin',
  'Ateliers': 'Ateliers',
  'Pilates Doux': 'Pilates Doux',
  'Power Pilates': 'Power Pilates',
  'Pilates - Tous niveaux': 'Pilates - Tous niveaux',
  'Autres': 'Autres'
};

const FALLBACK_CATEGORIES = Object.keys(FALLBACK_CATEGORY_COLORS);

// Custom styles for the calendar
const calendarStyles = {
  '.rbc-time-slot': {
    height: '35px',
    '@media (max-width: 768px)': {
      height: '20px'
    }
  },
  '.rbc-label': {
    fontSize: 'var(--time-slot-font-size, 0.85rem)',
    padding: '4px',
    minWidth: '150px',
    '@media (max-width: 768px)': {
      padding: '1px 3px',
      minWidth: '28px',
      fontSize: '0.6rem',
      width: 'auto',
      textAlign: 'right'
    }
  },
  '.rbc-time-gutter': {
    '@media (max-width: 768px)': {
      width: '32px',
      minWidth: '32px'
    }
  },
  '.rbc-time-content': {
    fontSize: 'var(--time-slot-font-size, 0.85rem)',
    '@media (max-width: 768px)': {
      fontSize: '0.6rem',
      marginLeft: '-1px'
    }
  },
  '.rbc-time-header-cell': {
    '@media (max-width: 768px)': {
      height: '60px !important',
      minHeight: '60px !important',
      maxHeight: '60px !important'
    }
  },
  '.rbc-time-view': {
    fontSize: '0.85rem',
    height: '700px',
    width: '100%',
    '@media (max-width: 768px)': {
      fontSize: '0.7rem',
      height: 'calc(100vh - 180px)',
      minHeight: '520px',
      maxWidth: '100vw',
      overflowX: 'hidden',
      borderTop: '1px solid #ddd'
    }
  },
  '.rbc-timeslot-group': {
    minHeight: '35px',
    '@media (max-width: 768px)': {
      minHeight: '20px'
    }
  },
  '.rbc-time-column': {
    minWidth: '2000px',
    '@media (max-width: 768px)': {
      minWidth: 'auto',
      width: 'calc(100% - 32px)'
    }
  },
  '.rbc-day-slot': {
    minWidth: '2000px',
    '@media (max-width: 768px)': {
      minWidth: 'auto',
      width: '100%'
    }
  },
  '.rbc-time-header': {
    '@media (max-width: 768px)': {
      marginRight: '0',
      overflowX: 'hidden',
      borderBottom: '1px solid #DDD',
      height: '60px !important',
      minHeight: '60px !important',
      maxHeight: '60px !important'
    }
  },
  '.rbc-time-header-content': {
    '@media (max-width: 768px)': {
      width: '100%',
      flexShrink: 0,
      borderLeft: 'none',
      height: '60px !important',
      minHeight: '60px !important',
      maxHeight: '60px !important'
    }
  },
  '.rbc-header': {
    '@media (max-width: 768px)': {
      minWidth: '40px',
      fontSize: '0.75rem',
      padding: '0',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      height: '60px !important',
      minHeight: '60px !important',
      maxHeight: '60px !important',
      justifyContent: 'center',
      backgroundColor: '#f5f5f5',
      borderBottom: '1px solid #ddd',
      '& > span': {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        width: '100%',
        padding: '4px',
        '& > span': {
          display: 'block',
          width: '100%',
          textAlign: 'center',
          '&:first-of-type': {
            fontWeight: 600,
            marginBottom: '2px',
            fontSize: '1rem'
          },
          '&:last-of-type': {
            fontSize: '1.1rem',
            fontWeight: 500
          }
        }
      }
    }
  },
  '.rbc-event': {
    backgroundColor: 'transparent',
    border: 'none',
    '@media (max-width: 768px)': {
      position: 'absolute !important',
      width: '100% !important',
      left: '0 !important',
      right: '0 !important',
      margin: '0 !important'
    }
  },
  '.rbc-event-content': {
    whiteSpace: 'pre-wrap',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    '@media (max-width: 768px)': {
      lineHeight: 1.2,
      fontSize: '0.75rem'
    },
    lineHeight: 1.3,
    fontSize: '0.8rem',
    maxHeight: '5.2em',
    overflow: 'hidden'
  },
  '.rbc-events-container': {
    '@media (max-width: 768px)': {
      width: '100% !important',
      margin: '0 !important'
    }
  },
  '.rbc-day-slot .rbc-events-container': {
    '@media (max-width: 768px)': {
      width: '100% !important',
      margin: '0 !important'
    }
  },
  '.rbc-day-slot .rbc-event': {
    '@media (max-width: 768px)': {
      width: '100% !important',
      left: '0 !important',
      right: '0 !important',
      padding: '1px 2px !important'
    }
  },
  '.rbc-event-overlaps': {
    width: '50% !important',
    zIndex: 2
  },
  '.rbc-event-continues-after': {
    borderBottomRightRadius: 0,
    borderBottomLeftRadius: 0
  },
  '.rbc-day-slot .rbc-events-container .rbc-event.rbc-selected': {
    background: 'none'
  },
  '.rbc-event.rbc-selected': {
    backgroundColor: 'inherit',
    outline: 'none'
  },
  '.rbc-event:focus': {
    outline: 'none'
  },
  '.rbc-day-slot .rbc-events-container .rbc-event.rbc-event-overlaps': {
    '@media (max-width: 768px)': {
      width: '100% !important',
      left: '0 !important'
    }
  },
  '.rbc-day-slot .rbc-background-event': {
    '@media (max-width: 768px)': {
      width: '100% !important'
    }
  },
  '.rbc-day-slot .rbc-event-content': {
    '@media (max-width: 768px)': {
      width: '100%'
    }
  },
  '.rbc-day-slot .rbc-event-continues-before': {
    borderTopRightRadius: 0,
    borderTopLeftRadius: 0
  },
  '.rbc-day-slot .rbc-event-continues-earlier': {
    borderTopRightRadius: 0,
    borderTopLeftRadius: 0
  },
  '.rbc-day-slot .rbc-event-continues-later': {
    borderBottomRightRadius: 0,
    borderBottomLeftRadius: 0
  },
  '.rbc-toolbar': {
    '@media (max-width: 768px)': {
      flexWrap: 'wrap',
      justifyContent: 'center',
      gap: '8px',
      marginBottom: '8px',
      '& .rbc-btn-group': {
        margin: '4px',
        display: 'flex',
        flexWrap: 'nowrap',
        width: 'auto',
        '& button': {
          flex: '1',
          padding: '8px',
          minWidth: '60px',
          fontSize: '0.9rem',
          whiteSpace: 'nowrap',
          backgroundColor: '#F3E6D8',
          '&.rbc-active': {
            backgroundColor: '#942B0D',
            color: 'white'
          }
        }
      }
    }
  },
  '.rbc-toolbar-label': {
    '@media (max-width: 768px)': {
      order: -1,
      width: '100%',
      textAlign: 'center',
      fontSize: '1.1rem',
      padding: '8px 0',
      fontWeight: 500
    }
  },
  '.rbc-btn-group': {
    '@media (max-width: 768px)': {
      margin: '0',
      '& button': {
        padding: '4px 8px',
        fontSize: '0.8rem'
      }
    }
  },
  '.rbc-allday-cell': {
    '@media (max-width: 768px)': {
      display: 'none'
    }
  },
  '.rbc-time-view .rbc-header': {
    '@media (max-width: 768px)': {
      borderBottom: 'none'
    }
  },
  '.rbc-time-view .rbc-time-header': {
    '@media (max-width: 768px)': {
      marginRight: '0',
      overflowX: 'hidden',
      borderBottom: '1px solid #DDD'
    }
  },
  '.rbc-time-header-content': {
    '@media (max-width: 768px)': {
      width: '100%',
      flexShrink: 0,
      borderLeft: 'none'
    }
  },
};

const Activities = () => {
  const { t } = useTranslation();
  const { category: urlCategory } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { names, colors, displayNames, byName: typesByName, ready } = useActivityTypes();
  const categoryColors = ready ? { ...FALLBACK_CATEGORY_COLORS, ...colors } : FALLBACK_CATEGORY_COLORS;
  const categoryDisplayNames = ready ? { ...FALLBACK_CATEGORY_DISPLAY_NAMES, ...displayNames } : FALLBACK_CATEGORY_DISPLAY_NAMES;
  const categories = ready && names.length ? names : FALLBACK_CATEGORIES;
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    category: urlCategory ? decodeURIComponent(urlCategory) : '',
  });
  const [mobileView, setMobileView] = useState('list'); // 'list' or 'calendar'
  const [listViewMode, setListViewMode] = useState('day'); // 'day' or 'week'

  // Initialize with current day for mobile list view
  const today = new Date();
  const currentWeekStart = startOfWeek(today, { weekStartsOn: 1 });
  const currentWeekEnd = endOfWeek(today, { weekStartsOn: 1 });

  // Check if device is mobile
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [visibleRange, setVisibleRange] = useState({
    start: isMobile ? startOfDay(today) : currentWeekStart,
    end: isMobile ? endOfDay(today) : currentWeekEnd
  });
  const [calendarView, setCalendarView] = useState('week');
  const [currentDate, setCurrentDate] = useState(today);

  // Update filters when URL or location state changes
  useEffect(() => {
    const categoryFromUrl = urlCategory ? decodeURIComponent(urlCategory) : '';
    const categoryFromState = location.state?.selectedCategory || '';
    const selectedCategory = categoryFromUrl || categoryFromState;
    
    if (selectedCategory && selectedCategory !== filters.category) {
      setFilters(prev => ({
        ...prev,
        category: selectedCategory
      }));
    }
  }, [urlCategory, location.state, location.key, filters.category, location]);

  // Initial fetch and setup
  useEffect(() => {
    const fetchInitialActivities = async () => {
      try {
        setLoading(true);
        let params = {
          status: 'published',
        };

        if (filters.category) {
          params.category = filters.category;
        }

        const response = await axios.get('/api/activities', { params });
        
        if (response.data.length > 0) {
          const now = new Date();
          now.setHours(0, 0, 0, 0); // Start of today

          if (isMobile && mobileView === 'list') {
            // For mobile list view, set range to today
            setCurrentDate(now);
            setVisibleRange({
              start: startOfDay(now),
              end: endOfDay(now)
            });
          } else {
            // For calendar view or desktop, use week view
            const futureActivities = response.data
              .filter(activity => new Date(activity.date) >= now)
              .sort((a, b) => new Date(a.date) - new Date(b.date));
            
            if (futureActivities.length > 0) {
              const earliestFutureActivity = futureActivities[0];
              const earliestDate = new Date(earliestFutureActivity.date);
              const weekStart = startOfWeek(earliestDate, { weekStartsOn: 1 });
              setCurrentDate(weekStart);
              setVisibleRange({
                start: weekStart,
                end: endOfWeek(weekStart, { weekStartsOn: 1 })
              });
            } else {
              const weekStart = startOfWeek(now, { weekStartsOn: 1 });
              setCurrentDate(weekStart);
              setVisibleRange({
                start: weekStart,
                end: endOfWeek(weekStart, { weekStartsOn: 1 })
              });
            }
          }
        }
        
        setActivities(response.data);
        setError(null);
      } catch (error) {
        setError(t('errorLoading'));
      } finally {
        setLoading(false);
      }
    };

    fetchInitialActivities();
  }, [filters.category, calendarView, t, isMobile, mobileView]);

  // Update visible range when switching views
  useEffect(() => {
    if (isMobile) {
      const now = new Date();
      if (mobileView === 'list') {
        // Always set to today's range for list view
        setVisibleRange({
          start: startOfDay(now),
          end: endOfDay(now)
        });
      } else {
        // Set to current week for calendar view
        const weekStart = startOfWeek(now, { weekStartsOn: 1 });
        setVisibleRange({
          start: weekStart,
          end: endOfWeek(weekStart, { weekStartsOn: 1 })
        });
      }
    }
  }, [mobileView, isMobile]);

  // Fetch activities when component mounts or filters change
  useEffect(() => {
    const fetchActivities = async () => {
      try {
        const params = {
          ...filters,
          status: 'published',
          limit: 2000 // Limit to 2000 activities
        };

        // Calculate date range for calendar view
        const now = new Date();
        const extendedStart = new Date(now);
        const extendedEnd = new Date(now);
        
        // Set range to 30 days before and 90 days after
        extendedStart.setDate(extendedStart.getDate() - 30);
        extendedEnd.setDate(extendedEnd.getDate() + 90);
        
        // Set time boundaries
        extendedStart.setHours(0, 0, 0, 0);
        extendedEnd.setHours(23, 59, 59, 999);
        
        params.startDate = extendedStart.toISOString();
        params.endDate = extendedEnd.toISOString();

        const response = await axios.get('/api/activities', { params });
        setActivities(response.data);
      } catch (error) {
        setError(t('errorLoading'));
      }
    };

    fetchActivities();
  }, [filters, calendarView, t]);

  // Get visible activities based on current view
  const getVisibleActivities = () => {
    if (!visibleRange.start || !visibleRange.end) return activities;

    // For mobile list view
    if (isMobile && mobileView === 'list') {
      const viewStart = startOfDay(visibleRange.start);
      const viewEnd = endOfDay(visibleRange.end);
      
      return activities.filter(activity => {
        const activityDate = new Date(activity.date);
        return activityDate >= viewStart && activityDate <= viewEnd;
      });
    }

    // For calendar view or desktop, use the visible range
    const startTime = visibleRange.start.getTime();
    const endTime = visibleRange.end.getTime();

    return activities.filter(activity => {
      const activityDate = new Date(activity.date).getTime();
      return activityDate >= startTime && activityDate <= endTime;
    });
  };

  // Handle date change in list view
  const handleListDateChange = (newDate) => {
    const start = startOfDay(newDate);
    const end = endOfDay(newDate);
    setCurrentDate(newDate);
    setVisibleRange({ start, end });
  };

  // Get upcoming activities for the visible range
  const getUpcomingVisibleActivities = () => {
    const now = new Date().getTime();
    const startTime = visibleRange.start.getTime();
    const endTime = visibleRange.end.getTime();

    return activities
      .filter(activity => {
        const activityDate = new Date(activity.date).getTime();
        if (calendarView === 'month') {
          // For month view, show all upcoming activities in the current month
          const activityMonth = new Date(activity.date).getMonth();
          const currentMonth = new Date(currentDate).getMonth();
          return activityDate >= now && activityMonth === currentMonth;
        } else {
          // For week and day views, show activities within the visible range
          return activityDate >= now && activityDate >= startTime && activityDate <= endTime;
        }
      })
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  // Get past activities for the visible range
  const getPastVisibleActivities = () => {
    const now = new Date().getTime();
    const startTime = visibleRange.start.getTime();
    const endTime = visibleRange.end.getTime();

    return activities
      .filter(activity => {
        const activityDate = new Date(activity.date).getTime();
        if (calendarView === 'month') {
          // For month view, show all past activities in the current month
          const activityMonth = new Date(activity.date).getMonth();
          const currentMonth = new Date(currentDate).getMonth();
          return activityDate < now && activityMonth === currentMonth;
        } else {
          // For week and day views, show activities within the visible range
          return activityDate < now && activityDate >= startTime && activityDate <= endTime;
        }
      })
      .sort((a, b) => new Date(b.date) - new Date(a.date));
  };

  const handleFilterChange = (field, value) => {
    if (field === 'category') {
      if (value) {
        navigate(`/activities/category/${encodeURIComponent(value)}`, { replace: true });
      } else {
        navigate('/activities', { replace: true });
      }
    }
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleResetFilters = () => {
    setFilters({ category: '' });
    navigate('/activities', { replace: true });
  };

  // Style public holidays
  const dayPropGetter = (date) => {
    if (isFrenchPublicHoliday(date)) {
      return {
        style: {
          backgroundColor: '#2E7D32',
          color: 'white',
        },
      };
    }
    return {};
  };

  // Handle calendar navigation
  const handleNavigate = (newDate) => {
    setCurrentDate(newDate);
    
    let start, end;
    
    switch (calendarView) {
      case 'month':
        start = startOfMonth(newDate);
        end = endOfMonth(newDate);
        break;
      case 'week':
        start = startOfWeek(newDate, { weekStartsOn: 1 });
        end = endOfWeek(newDate, { weekStartsOn: 1 });
        break;
      default: // day view
        start = startOfDay(newDate);
        end = endOfDay(newDate);
    }

    setVisibleRange({ start, end });
    
    // Update filters to fetch activities for the new date range
    setFilters(prev => ({
      ...prev,
      startDate: start.toISOString(),
      endDate: end.toISOString()
    }));
  };

  // Handle calendar view change
  const handleViewChange = (newView) => {
    setCalendarView(newView);
    
    // For day view, set the exact current date and time
    const now = new Date();
    const dateToUse = (newView === 'day') ? now : (newView === 'week' ? now : currentDate);
    setCurrentDate(dateToUse);
    
    // Update visible range based on new view
    let start, end;
    
    switch (newView) {
      case 'month':
        start = startOfMonth(dateToUse);
        end = endOfMonth(dateToUse);
        break;
      case 'week':
        start = startOfWeek(dateToUse, { weekStartsOn: 1 });
        end = endOfWeek(dateToUse, { weekStartsOn: 1 });
        break;
      default: // day view
        // For day view, use the exact current time
        start = startOfDay(dateToUse);
        end = endOfDay(dateToUse);
    }
    
    setVisibleRange({ start, end });
    
    // Update filters to fetch activities for the new date range
    setFilters(prev => ({
      ...prev,
      startDate: start.toISOString(),
      endDate: end.toISOString()
    }));
  };

  const handleModifyParticipants = async (activityId, newNumberOfParticipants) => {
    try {
      await axios.put(`/api/activities/${activityId}/participants`, {
        newNumberOfParticipants
      });
      
      // Refresh activities list
      const response = await axios.get('/api/activities', { params: { status: 'published' } });
      setActivities(response.data);
      
      toast.success(t('participantsModifiedSuccess'));
    } catch (error) {
      toast.error(error.response?.data?.message || t('errorModifyingParticipants'));
      throw error;
    }
  };

  return (
    <Container maxWidth="xl" sx={{ 
      '@media (max-width: 768px)': { 
        padding: '8px'
      }
    }}>
      {/* Header Section */}
      <Box textAlign="center" mb={4}>
        <Typography 
          variant="h2" 
          component="h1" 
          gutterBottom
          sx={{
            '@media (max-width: 768px)': {
              fontSize: '1.75rem',
              marginBottom: '1rem'
            }
          }}
        >
          {t('activitiesTitle')}
        </Typography>
        <Typography 
          variant="h5" 
          color="text.secondary" 
          mb={2}
          sx={{
            '@media (max-width: 768px)': {
              fontSize: '1rem',
              marginBottom: '0.5rem'
            }
          }}
        >
          {t('activitiesDescription')}
        </Typography>
      </Box>

      <Box sx={{ 
        py: 3,
        '@media (max-width: 768px)': {
          py: 1
        }
      }}>
        <Stack spacing={3}>
          {error && <Alert severity="error">{error}</Alert>}
          
          <Box sx={{ 
            display: 'flex', 
            gap: 2, 
            alignItems: 'center',
            flexWrap: 'wrap',
            '@media (max-width: 768px)': {
              flexDirection: 'column',
              gap: 1
            }
          }}>
            <FormControl sx={{ 
              minWidth: 200,
              '@media (max-width: 768px)': {
                width: '100%',
                minWidth: 'auto'
              }
            }}>
              <InputLabel>{t('category')}</InputLabel>
              <Select
                value={filters.category}
                onChange={(e) => handleFilterChange('category', e.target.value)}
                label={t('category')}
              >
                <MenuItem value="">{t('allCategories')}</MenuItem>
                {categories.map((category) => (
                  <MenuItem key={category} value={category}>
                    {categoryDisplayNames[category]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {filters.category && (
              <Tooltip title={t('clearFilters')}>
                <IconButton onClick={handleResetFilters} size="small">
                  <ClearIcon />
                </IconButton>
              </Tooltip>
            )}
            {/* Mobile View Toggle */}
            {isMobile && (
              <ToggleButtonGroup
                value={mobileView}
                exclusive
                onChange={(e, newView) => newView && setMobileView(newView)}
                size="small"
                sx={{ 
                  ml: 'auto',
                  '@media (max-width: 768px)': {
                    width: '100%',
                    '& .MuiToggleButton-root': {
                      flex: 1
                    }
                  }
                }}
              >
                <ToggleButton value="list">
                  <ViewListIcon sx={{ mr: 1 }} />
                  {t('listView')}
                </ToggleButton>
                <ToggleButton value="calendar">
                  <CalendarIcon sx={{ mr: 1 }} />
                  {t('calendarView')}
                </ToggleButton>
              </ToggleButtonGroup>
            )}
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {isMobile ? (
                mobileView === 'list' ? (
                  <Paper elevation={3} sx={{ p: 0 }}>
                    <MobileActivityList 
                      activities={getVisibleActivities()}
                      currentDate={currentDate}
                      onDateChange={handleListDateChange}
                    />
                  </Paper>
                ) : (
                  <Paper elevation={3} sx={{ 
                    p: 1, 
                    '@media (max-width: 768px)': {
                      p: 0,
                      overflowX: 'hidden',
                      width: '100%',
                      height: 'calc(100vh - 100px)',
                      minHeight: '600px'
                    }
                  }}>
                    <Calendar
                      localizer={localizer}
                      defaultView="week"
                      view={calendarView}
                      views={['month', 'week', 'day']}
                      events={activities.map((activity) =>
                        buildCalendarEvent(activity, { displayNames: categoryDisplayNames, typesByName, t })
                      )}
                      startAccessor="start"
                      endAccessor="end"
                      step={60}
                      timeslots={1}
                                          min={new Date(new Date().setHours(7, 0, 0))}
                    max={new Date(new Date().setHours(21, 0, 0))}
                    scrollToTime={new Date(new Date().setHours(7, 0, 0))}
                      date={currentDate}
                      onView={(newView) => {
                        setCalendarView(newView);
                        handleViewChange(newView);
                      }}
                      onNavigate={handleNavigate}
                      dayLayoutAlgorithm="no-overlap"
                      style={{ 
                        height: isMobile ? 'calc(100vh - 120px)' : '700px',
                        minHeight: isMobile ? '550px' : '700px',
                        width: '100%',
                        '--time-slot-font-size': isMobile ? '0.7rem' : '0.85rem',
                        overflowX: isMobile ? 'hidden' : 'auto',
                        ...calendarStyles
                      }}
                      toolbar={true}
                      eventPropGetter={(event) => ({
                        style: {
                          backgroundColor: categoryColors[event.category] || '#808080',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '3px',
                          whiteSpace: 'pre-line',
                          padding: isMobile ? '1px 2px' : '4px',
                          fontSize: isMobile ? '0.75rem' : '0.8rem',
                          lineHeight: isMobile ? 1.2 : 1.3,
                          maxHeight: '5.2em',
                          overflow: 'hidden',
                          position: 'absolute',
                          width: '100%',
                          left: 0,
                          right: 0,
                          margin: 0
                        }
                      })}
                      dayPropGetter={dayPropGetter}
                      onSelectEvent={event => navigate(`/activities/${event.slug}`)}
                      messages={{
                        month: t('monthView'),
                        week: t('weekView'),
                        day: t('dayView'),
                        today: t('today'),
                        previous: t('previous'),
                        next: t('next')
                      }}
                      formats={{
                        eventTimeRangeFormat: () => '',
                        timeGutterFormat: (date, culture, localizer) =>
                          isMobile ? localizer.format(date, 'HH', culture) : `${localizer.format(date, 'HH', culture)} h`,
                        dayFormat: (date, culture, localizer) =>
                          isMobile ? 
                            `${localizer.format(date, 'EEE', culture)}\n${localizer.format(date, 'd', culture)}` :
                            `${localizer.format(date, 'EEEE', culture)} ${localizer.format(date, 'd', culture)}`
                      }}
                      components={{
                        timeGutterHeader: () => null,
                        header: ({ label, date }) => {
                          if (isMobile) {
                            const dayName = format(date, 'EEE', { locale: fr });
                            const dayNumber = format(date, 'd', { locale: fr });
                            return (
                              <span style={{ 
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '100%',
                                width: '100%'
                              }}>
                                <span>{dayName}</span>
                                <span>{dayNumber}</span>
                              </span>
                            );
                          }
                          return <span>{label}</span>;
                        }
                      }}
                      culture="fr"
                    />
                  </Paper>
                )
              ) : (
                // Desktop view - existing calendar implementation
                <Paper elevation={3} sx={{ p: 2 }}>
                  <Calendar
                    localizer={localizer}
                    defaultView="week"
                    view={calendarView}
                    views={['month', 'week', 'day']}
                    events={activities.map((activity) =>
                      buildCalendarEvent(activity, { displayNames: categoryDisplayNames, typesByName, t })
                    )}
                    startAccessor="start"
                    endAccessor="end"
                    step={30}
                    timeslots={2}
                    min={new Date(new Date().setHours(7, 0, 0))}
                    max={new Date(new Date().setHours(21, 0, 0))}
                    scrollToTime={new Date(new Date().setHours(7, 0, 0))}
                    date={currentDate}
                    onView={(newView) => {
                      setCalendarView(newView);
                      handleViewChange(newView);
                    }}
                    onNavigate={handleNavigate}
                    dayLayoutAlgorithm="no-overlap"
                    style={{ 
                      height: '700px',
                      width: '100%',
                      '--time-slot-font-size': '0.85rem',
                      ...calendarStyles
                    }}
                    toolbar={true}
                    eventPropGetter={(event) => ({
                      style: {
                        backgroundColor: categoryColors[event.category] || '#808080',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '3px',
                        whiteSpace: 'pre-line',
                        padding: '4px',
                        fontSize: '0.8rem',
                        lineHeight: 1.3,
                        maxHeight: '5.2em',
                        overflow: 'hidden'
                      }
                    })}
                    dayPropGetter={dayPropGetter}
                    onSelectEvent={event => navigate(`/activities/${event.slug}`)}
                    messages={{
                      month: t('monthView'),
                      week: t('weekView'),
                      day: t('dayView'),
                      today: t('today'),
                      previous: t('previous'),
                      next: t('next')
                    }}
                    formats={{
                      eventTimeRangeFormat: () => '',
                      timeGutterFormat: (date, culture, localizer) =>
                        isMobile ? localizer.format(date, 'HH', culture) : `${localizer.format(date, 'HH', culture)} h`,
                      dayFormat: (date, culture, localizer) =>
                        isMobile ? 
                          `${localizer.format(date, 'EEE', culture)}\n${localizer.format(date, 'd', culture)}` :
                          `${localizer.format(date, 'EEEE', culture)} ${localizer.format(date, 'd', culture)}`
                    }}
                    components={{
                      timeGutterHeader: () => null,
                      header: ({ label, date }) => {
                        if (isMobile) {
                          const dayName = format(date, 'EEE', { locale: fr });
                          const dayNumber = format(date, 'd', { locale: fr });
                          return (
                            <span style={{ 
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              height: '100%',
                              width: '100%'
                            }}>
                              <span>{dayName}</span>
                              <span>{dayNumber}</span>
                            </span>
                          );
                        }
                        return <span>{label}</span>;
                      }
                    }}
                    culture="fr"
                  />
                </Paper>
              )}

              {/* Activity Cards Section - show only in list view on mobile */}
              {(!isMobile || mobileView === 'list') && (
                <Box sx={{ 
                  mt: 4,
                  '@media (max-width: 768px)': {
                    mt: 2
                  }
                }}>
                  {/* Upcoming Activities */}
                  <Box sx={{ mb: 6 }}>
                    <Typography variant="h5" component="h2" gutterBottom>
                      {t('upcomingActivities')} - {calendarView === 'month' 
                        ? t('monthView')
                        : calendarView === 'week'
                        ? t('weekView')
                        : t('dayView')}
                    </Typography>
                    <Grid container spacing={3}>
                      {getUpcomingVisibleActivities().map((activity) => (
                        <Grid item xs={12} sm={6} md={4} key={activity._id}>
                          <ActivityCard 
                            activity={activity}
                            onModifyParticipants={handleModifyParticipants}
                            numberOfParticipants={activity.numberOfParticipants}
                            showCancelButton={activity.isRegistered}
                          />
                        </Grid>
                      ))}
                      {getUpcomingVisibleActivities().length === 0 && (
                        <Grid item xs={12}>
                          <Typography align="center" color="text.secondary">
                            {t('noUpcomingActivities')}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Box>

                  {/* Past Activities */}
                  <Box>
                    <Typography variant="h5" component="h2" gutterBottom>
                      {t('pastActivities')} - {calendarView === 'month' 
                        ? t('monthView')
                        : calendarView === 'week'
                        ? t('weekView')
                        : t('dayView')}
                    </Typography>
                    <Grid container spacing={3}>
                      {getPastVisibleActivities().map((activity) => (
                        <Grid item xs={12} sm={6} md={4} key={activity._id}>
                          <ActivityCard 
                            activity={activity}
                            onModifyParticipants={handleModifyParticipants}
                            numberOfParticipants={activity.numberOfParticipants}
                            showCancelButton={activity.isRegistered}
                          />
                        </Grid>
                      ))}
                      {getPastVisibleActivities().length === 0 && (
                        <Grid item xs={12}>
                          <Typography align="center" color="text.secondary">
                            {t('noPastActivities')}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                </Box>
              )}
            </>
          )}
        </Stack>
      </Box>
    </Container>
  );
};

export default Activities; 